import React, { useEffect, useState } from "react";
import { Hero } from "../components/Hero";
import { ShopCard } from "../components/ShopCard";
import { ProductCard } from "../components/ProductCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Store, Package, Zap, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Shop, Product } from "@/src/types";
import { collection, query, limit, getDocs, orderBy } from "firebase/firestore";
import { db as firestoreDb } from "@/src/lib/firebase";

export function HomePage() {
  const [shops, setShops] = useState<Shop[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch featured shops
        const shopsSnap = await getDocs(collection(firestoreDb, "shops"));
        const allShops = shopsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Shop));
        
        // Prioritize premium shops
        const sortedShops = [...allShops].sort((a, b) => {
          if (a.plan === "premium" && b.plan !== "premium") return -1;
          if (a.plan !== "premium" && b.plan === "premium") return 1;
          return 0;
        }).slice(0, 6);
        
        setShops(sortedShops);

        // Fetch recent products
        const productsSnap = await getDocs(collection(firestoreDb, "products"));
        const allProducts = productsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Product));
        
        // Prioritize products from premium shops
        // Create a map for quick lookup of shop plans
        const shopPlanMap = new Map(allShops.map(s => [s.id, s.plan]));
        
        const sortedProducts = [...allProducts].sort((a, b) => {
          const planA = shopPlanMap.get(a.shopId) || "free";
          const planB = shopPlanMap.get(b.shopId) || "free";
          
          if (planA === "premium" && planB !== "premium") return -1;
          if (planB === "premium" && planA !== "premium") return 1;
          return 0;
        }).slice(0, 8);
        
        setProducts(sortedProducts);
      } catch (error) {
        console.error("Error fetching home data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="flex flex-col gap-16 pb-16">
      <Hero />

      {/* Featured Shops */}
      <section className="container px-4 md:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Tiendas Destacadas</h2>
            <p className="text-muted-foreground mt-1">Los emprendedores más populares de nuestra comunidad.</p>
          </div>
          <Link to="/shops">
            <Button variant="ghost" className="hidden sm:flex">
              Ver todas <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : shops.length === 0 ? (
          <div className="text-center py-12 bg-muted/30 rounded-xl">
            <p className="text-muted-foreground">Aún no hay tiendas registradas.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {shops.map((shop) => (
              <ShopCard key={shop.id} shop={shop} />
            ))}
          </div>
        )}
      </section>

      {/* Popular Products */}
      <section className="container px-4 md:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Productos Populares</h2>
            <p className="text-muted-foreground mt-1">Lo que todos están comprando esta semana.</p>
          </div>
          <Link to="/products">
            <Button variant="ghost" className="hidden sm:flex">
              Ver todos <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 bg-muted/30 rounded-xl">
            <p className="text-muted-foreground">Aún no hay productos registrados.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </section>

      {/* Mall Virtual Banner */}
      <section className="container px-4 md:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-600 to-primary p-8 md:p-12 text-white">
          <div className="relative z-10 max-w-2xl">
            <Badge className="mb-4 bg-white/20 hover:bg-white/30 text-white border-none px-3 py-1">Novedad</Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tighter mb-4">Únete al Mall Virtual</h2>
            <p className="text-white/80 text-lg mb-8 leading-relaxed">
              Consigue tu tarjeta virtual de fidelización y accede a eventos exclusivos, preventas y promociones 
              únicas en tus tiendas favoritas. ¡Es gratis y te premiamos por tu fidelidad!
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/club">
                <Button variant="secondary" size="lg" className="font-bold group">
                  Obtener mi tarjeta <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/club">
                <Button variant="ghost" className="text-white hover:bg-white/10 decoration-white">Ver eventos próximos</Button>
              </Link>
            </div>
          </div>
          {/* Abstract background shapes */}
          <div className="absolute -right-20 -bottom-20 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute right-20 top-0 w-40 h-40 bg-indigo-400/20 rounded-full blur-2xl" />
        </div>
      </section>

      {/* Features / Why Us */}
      <section className="bg-muted/50 py-16">
        <div className="container px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold tracking-tight">¿Por qué comprar en EmpreMarket?</h2>
            <p className="text-muted-foreground mt-4">
              Conectamos personas con historias, no solo con productos. 
              Cada compra tiene un impacto real en la economía local.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            <div className="flex flex-col items-center text-center p-6 bg-background rounded-xl shadow-sm">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Store className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Apoyo Local</h3>
              <p className="text-sm text-muted-foreground">
                El 100% de tu dinero va directamente al emprendedor, ayudando a crecer negocios locales.
              </p>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-background rounded-xl shadow-sm">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Conexión Directa</h3>
              <p className="text-sm text-muted-foreground">
                Chatea directamente con los vendedores para personalizar tus pedidos o resolver dudas.
              </p>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-background rounded-xl shadow-sm">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Package className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Variedad Única</h3>
              <p className="text-sm text-muted-foreground">
                Encuentra productos y servicios que no verás en grandes tiendas departamentales.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container px-4 md:px-8">
        <div className="bg-primary rounded-3xl p-8 md:p-16 text-primary-foreground flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">¿Tienes un emprendimiento?</h2>
            <p className="text-primary-foreground/80 text-lg">
              Únete a nuestra comunidad y empieza a vender en minutos. 
              Te damos todas las herramientas para que tu negocio digital despegue.
            </p>
          </div>
          <Link to="/become-seller">
            <Button variant="secondary" size="lg" className="h-14 px-10 text-lg font-bold">
              Crear mi tienda gratis
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
